import React, { useState, useEffect } from "react";
import ProductRowActionMenu from "./ProductRowActionMenu";
import "../settings/Settings.css";
import "./Inventory.css";

const InventoryTable = ({
  products,
  isLoading,
  error,
  openMenuId,
  actionMenuRefs,
  toggleRowActions,
  handleEditProduct,
  handleSoftDeleteProduct,
  handleRestoreProduct,
  handleViewProduct,
  setOpenMenuId,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");

  const totalPages = Math.ceil(products.length / itemsPerPage);

  const sortedProducts = [...products].sort((a, b) => {
    if (!sortColumn) return 0;

    let valA = a[sortColumn];
    let valB = b[sortColumn];

    if (sortColumn === "distributorCompanyName") {
      valA = a.distributor ? a.distributor.companyName : "";
      valB = b.distributor ? b.distributor.companyName : "";
    }

    if (sortColumn === "price" || sortColumn === "totalStocks") {
      valA = parseFloat(valA);
      valB = parseFloat(valB);
    }

    if (valA < valB) {
      return sortDirection === "asc" ? -1 : 1;
    }
    if (valA > valB) {
      return sortDirection === "asc" ? 1 : -1;
    }
    return 0;
  });

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = sortedProducts.slice(
    indexOfFirstItem,
    indexOfLastItem
  );

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    } else if (totalPages === 0 && currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [products.length, totalPages, currentPage]);

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prev) => prev - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prev) => prev + 1);
    }
  };

  const paginate = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const renderSortIndicator = (column) => {
    if (sortColumn === column) {
      return sortDirection === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  // Helper to determine the color class for the stock cell
  const getStockCellClass = (product) => {
    if (!product.optimalStock || product.optimalStock <= 0) {
      return "";
    }

    const stockPercentage = (product.totalStocks / product.optimalStock) * 100;

    if (stockPercentage < 10) {
      return "low-stock-red";
    }
    if (stockPercentage < 25) {
      return "low-stock-orange";
    }
    if (stockPercentage < 50) {
      return "low-stock-yellow";
    }
    return "";
  };

  if (isLoading) {
    return <p className="loading-message">Loading products...</p>;
  }

  if (error) {
    return <p className="error-message">{error}</p>;
  }

  return (
    <div className="table-container">
      <table className="user-table">
        <thead>
          <tr>
            <th onClick={() => handleSort("brandName")}>
              Description {renderSortIndicator("brandName")}
            </th>
            <th onClick={() => handleSort("distributorCompanyName")}>
              Distributor {renderSortIndicator("distributorCompanyName")}
            </th>
            <th onClick={() => handleSort("price")}>
              Price {renderSortIndicator("price")}
            </th>
            <th onClick={() => handleSort("totalStocks")}>
              Total Stocks {renderSortIndicator("totalStocks")}
            </th>
            <th className="actions-header">Actions</th>
          </tr>
        </thead>
        <tbody>
          {currentProducts.length > 0 ? (
            currentProducts.map((product) => (
              <tr
                key={product.id}
                onDoubleClick={() => handleViewProduct(product)}
              >
                <td>
                  <strong>{product.brandName}</strong> ({product.genericName})
                  <br />
                  {product.manufacturer}, {product.category}, {product.type},{" "}
                  {product.content}
                </td>
                <td>
                  {product.distributor
                    ? product.distributor.companyName
                    : "N/A"}
                </td>
                <td>
                  ₱
                  {product.price
                    ? parseFloat(product.price).toFixed(2)
                    : "0.00"}
                </td>
                {/* Apply the class to this specific <td> tag */}
                <td className={getStockCellClass(product)}>
                  {product.totalStocks}
                </td>
                <ProductRowActionMenu
                  product={product}
                  openMenuId={openMenuId}
                  actionMenuRefs={actionMenuRefs}
                  toggleRowActions={toggleRowActions}
                  handleEditProduct={handleEditProduct}
                  handleSoftDeleteProduct={handleSoftDeleteProduct}
                  handleRestoreProduct={handleRestoreProduct}
                  setOpenMenuId={setOpenMenuId}
                  handleViewProduct={handleViewProduct}
                />
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="no-data">
                No products found.
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="pagination">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 1}
            className="pagination-button"
          >
            Previous
          </button>
          {[...Array(totalPages).keys()].map((number) => (
            <button
              key={number + 1}
              onClick={() => paginate(number + 1)}
              className={`pagination-button ${
                currentPage === number + 1 ? "active" : ""
              }`}
            >
              {number + 1}
            </button>
          ))}
          <button
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
            className="pagination-button"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default InventoryTable;
